import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, RefreshCw, Unlink, AlertTriangle, Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "Mai";

export function GcalPanel() {
  const [st, setSt] = useState(null);
  const [f, setF] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [busy, setBusy] = useState(false);
  const [connectError, setConnectError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/gcal/status");
      setSt(data);
      setF({ ...data.settings, gcal_calendar_id: data.calendar_id });
    } catch { toast.error("Errore nel caricamento dello stato Google Calendar"); }
  };
  useEffect(() => {
    load();
    const q = new URLSearchParams(window.location.search);
    const g = q.get("gcal");
    if (g === "connected") {
      toast.success("Google Calendar collegato correttamente");
      window.history.replaceState({}, "", window.location.pathname + "?tab=settings");
    } else if (g === "error") {
      const REASONS = {
        invalid_client: "Client Google non valido: controlla Client ID e Secret.",
        invalid_grant: "Autorizzazione scaduta o già utilizzata: riprova il collegamento.",
        redirect_uri_mismatch: "URI di reindirizzamento non corrispondente: verifica l'URI registrato su Google Cloud Console.",
        calendar_api: "Calendar API non abilitata nel progetto Google Cloud.",
        save: "Errore nel salvataggio del collegamento.",
        stato: "Sessione di collegamento scaduta: riprova dalla dashboard.",
      };
      const msg = REASONS[q.get("reason")] || "Collegamento non riuscito: riprova dalla dashboard.";
      setConnectError(msg);
      toast.error(msg);
      window.history.replaceState({}, "", window.location.pathname + "?tab=settings");
    }
  }, []);

  if (!st || !f) return <p className="text-sm text-taupe">Caricamento…</p>;

  const STATE_LABELS = {
    non_configurato: "Non configurato", non_connesso: "Non connesso", connesso: "Connesso",
    errore: "Errore", da_riconnettere: "Da riconnettere",
  };
  const STATE_STYLE = {
    non_configurato: "text-amber-700 border-amber-700/40 bg-amber-50",
    non_connesso: "text-taupe border-hair bg-white",
    connesso: "text-emerald-700 border-emerald-700/40 bg-emerald-50",
    errore: "text-red-700 border-red-700/40 bg-red-50",
    da_riconnettere: "text-amber-700 border-amber-700/40 bg-amber-50",
  };
  const state = st.state || (st.configured ? (st.connected ? "connesso" : "non_connesso") : "non_configurato");

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/gcal/auth-url");
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossibile avviare il collegamento");
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Scollegare Google Calendar? Le prenotazioni esistenti restano nel database.")) return;
    setBusy(true);
    try {
      await api.post("/admin/gcal/disconnect");
      toast.success("Google Calendar scollegato");
      load();
    } catch { toast.error("Errore durante la disconnessione"); }
    finally { setBusy(false); }
  };

  const syncNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/gcal/sync");
      if (data.failed === 0) toast.success(`Sincronizzazione completata (${data.synced} aggiornate)`);
      else toast.warning(`Sincronizzate ${data.synced}, non riuscite ${data.failed}. Riprova più tardi.`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Sincronizzazione non riuscita"); }
    finally { setBusy(false); }
  };

  const loadCalendars = async () => {
    try {
      const { data } = await api.get("/admin/gcal/calendars");
      setCalendars(data.calendars);
      // Se la selezione non è mai stata salvata, fissa davvero il calendario principale:
      // il valore mostrato deve corrispondere a ciò che è salvato, altrimenti il sync non parte.
      if (!f.gcal_calendar_id && (data.calendars || []).length) {
        await api.put("/admin/settings", { ...f, gcal_calendar_id: "primary" });
        setF((prev) => ({ ...prev, gcal_calendar_id: "primary" }));
        toast.success("Calendario principale selezionato e salvato");
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Errore lettura calendari"); }
  };

  const save = async (extra = {}) => {
    setBusy(true);
    try {
      await api.put("/admin/settings", { ...f, ...extra });
      toast.success("Impostazioni salvate");
      load();
    } catch { toast.error("Errore salvataggio"); }
    finally { setBusy(false); }
  };

  const btn = "min-h-[44px] inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase px-4 py-2 transition-colors disabled:opacity-60";

  return (
    <section className="bg-white border border-hair p-5 md:p-8" data-testid="settings-section-gcal">
      <div className="eyebrow flex items-center gap-2"><CalendarDays size={14} className="text-gold" /> Google Calendar</div>

      <div className="mt-4 flex items-center gap-3 flex-wrap" data-testid="gcal-state">
        <span className={`text-[10px] tracking-[0.14em] uppercase border px-3 py-1.5 ${STATE_STYLE[state]}`} data-testid="gcal-state-badge">{STATE_LABELS[state]}</span>
        {st.account_email && state !== "non_configurato" && <span className="text-xs text-taupe">{st.account_email}</span>}
        {st.last_sync && <span className="text-xs text-taupe">Ultima sync: {fmtDate(st.last_sync)}</span>}
      </div>
      <p className="text-[11px] text-taupe mt-3">URI di reindirizzamento da registrare su Google Cloud Console: <code className="break-all" data-testid="gcal-redirect-uri">{st.redirect_uri}</code></p>
      {(st.missing_vars || []).length > 0 && (
        <p className="text-[11px] text-amber-800 mt-2" data-testid="gcal-missing-vars">Variabili d'ambiente mancanti sul server: {(st.missing_vars || []).join(", ")} (i valori non vengono mai mostrati né esportati)</p>
      )}

      {!st.configured ? (
        <div className="mt-6 border border-amber-700/40 bg-amber-50 p-5" data-testid="gcal-not-configured">
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className="text-amber-700 mt-0.5 shrink-0" />
            <div className="text-sm leading-relaxed">
              <p className="font-medium">Credenziali Google non configurate sul server</p>
              <p className="text-taupe mt-2">Per collegare il calendario servono due variabili d'ambiente (mai nel codice, mai nei backup):</p>
              <ol className="list-decimal ml-5 mt-2 space-y-1 text-taupe">
                <li>Google Cloud Console → crea un progetto e abilita <strong>Google Calendar API</strong></li>
                <li>OAuth consent screen → External → aggiungi scope <code>calendar</code> e la tua email come test user</li>
                <li>Credenziali → OAuth client ID (Web application) con URI di reindirizzamento: <code className="break-all">{st.redirect_uri}</code></li>
                <li>Imposta <code>GOOGLE_CLIENT_ID</code> e <code>GOOGLE_CLIENT_SECRET</code> nelle variabili d'ambiente del server e riavvia</li>
              </ol>
            </div>
          </div>
        </div>
      ) : (state === "non_connesso" || state === "da_riconnettere") ? (
        <div className="mt-6" data-testid="gcal-disconnected">
          {connectError && (
            <div className="mb-4 border border-red-700/40 bg-red-50 p-4 text-sm text-red-900 flex items-start gap-3" data-testid="gcal-connect-error">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" /> {connectError}
            </div>
          )}
          <p className="text-sm text-taupe">{state === "da_riconnettere"
            ? "L'autorizzazione Google non è più valida o è stata revocata. Ricollega l'account per riprendere la sincronizzazione; le prenotazioni restano al sicuro nel database."
            : "Nessun account collegato. Le prenotazioni funzionano comunque: il calendario serve per sincronizzare eventi e bloccare gli orari già impegnati."}</p>
          <button onClick={connect} disabled={busy} className={`${btn} bg-ink text-bone hover:bg-gold mt-4`} data-testid="gcal-connect-btn">
            <CalendarDays size={14} /> {state === "da_riconnettere" ? "Ricollega Google Calendar" : "Collega Google Calendar"}
          </button>
        </div>
      ) : (
        <div className="mt-6 space-y-6" data-testid="gcal-connected">
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div className="border border-hair p-4">
              <span className="text-[10px] tracking-[0.14em] uppercase text-taupe">Account collegato</span>
              <p className="mt-1 break-all" data-testid="gcal-account">{st.account_email || "—"}</p>
            </div>
            <div className="border border-hair p-4">
              <span className="text-[10px] tracking-[0.14em] uppercase text-taupe">Stato sincronizzazione</span>
              <p className="mt-1 flex items-center gap-2" data-testid="gcal-sync-state">
                {st.pending_count > 0
                  ? <><AlertTriangle size={14} className="text-amber-700" /> {st.pending_count} in attesa di sincronizzazione</>
                  : <><Check size={14} className="text-emerald-700" /> Sincronizzata</>}
              </p>
              <p className="text-xs text-taupe mt-1">Ultima sync: <span data-testid="gcal-last-sync">{fmtDate(st.last_sync)}</span></p>
            </div>
          </div>
          {st.last_error && (
            <div className="border border-amber-700/40 bg-amber-50 p-4 text-sm text-amber-900" data-testid="gcal-last-error">{st.last_error}</div>
          )}
          <div>
            <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Calendario utilizzato</Label>
            <div className="flex gap-2 mt-1">
              <Select value={f.gcal_calendar_id || "primary"} onValueChange={(v) => { setF({ ...f, gcal_calendar_id: v }); save({ gcal_calendar_id: v }); }}>
                <SelectTrigger className="rounded-none" data-testid="gcal-calendar-select" onClick={loadCalendars}>
                  <SelectValue placeholder="Scegli calendario" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="primary">Calendario principale</SelectItem>
                  {calendars.filter((c) => !c.primary).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <label className="flex items-center gap-3 text-sm cursor-pointer min-h-[44px]">
              <Switch checked={!!f.gcal_auto_sync} onCheckedChange={(v) => setF({ ...f, gcal_auto_sync: v })} data-testid="gcal-auto-sync" />
              <span>Sincronizzazione automatica</span>
            </label>
            <label className="flex items-center gap-3 text-sm cursor-pointer min-h-[44px]">
              <Switch checked={!!f.gcal_auto_create} onCheckedChange={(v) => setF({ ...f, gcal_auto_create: v })} data-testid="gcal-auto-create" />
              <span>Crea evento alla conferma</span>
            </label>
            <label className="flex items-center gap-3 text-sm cursor-pointer min-h-[44px]">
              <Switch checked={!!f.gcal_sync_update} onCheckedChange={(v) => setF({ ...f, gcal_sync_update: v })} data-testid="gcal-sync-update" />
              <span>Aggiorna/annulla eventi sincronizzati</span>
            </label>
            <label className="flex items-center gap-3 text-sm cursor-pointer min-h-[44px]">
              <Switch checked={!!f.gcal_meet_enabled} onCheckedChange={(v) => setF({ ...f, gcal_meet_enabled: v })} data-testid="gcal-meet" />
              <span>Aggiungi link Google Meet</span>
            </label>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Durata consulenza (minuti)</Label>
              <Input type="number" min={15} step={5} value={f.booking_duration_min} onChange={(e) => setF({ ...f, booking_duration_min: Number(e.target.value) })} className="rounded-none mt-1" data-testid="gcal-duration" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Pausa tra appuntamenti (minuti)</Label>
              <Input type="number" min={0} step={5} value={f.booking_buffer_min} onChange={(e) => setF({ ...f, booking_buffer_min: Number(e.target.value) })} className="rounded-none mt-1" data-testid="gcal-buffer" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Titolo evento (placeholder: {"{name}"}, {"{type}"})</Label>
              <Input value={f.gcal_event_title} onChange={(e) => setF({ ...f, gcal_event_title: e.target.value })} className="rounded-none mt-1" data-testid="gcal-event-title" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Promemoria (minuti prima, separati da virgola)</Label>
              <Input value={f.gcal_reminders} onChange={(e) => setF({ ...f, gcal_reminders: e.target.value })} className="rounded-none mt-1" placeholder="1440,60" data-testid="gcal-reminders" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Quando una prenotazione viene annullata</Label>
              <Select value={f.gcal_cancel_mode} onValueChange={(v) => setF({ ...f, gcal_cancel_mode: v })}>
                <SelectTrigger className="rounded-none mt-1" data-testid="gcal-cancel-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="delete">Elimina l'evento dal calendario</SelectItem>
                  <SelectItem value="cancel">Segna l'evento come annullato</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => save()} disabled={busy} className={`${btn} bg-ink text-bone hover:bg-gold`} data-testid="gcal-save-btn">Salva impostazioni</button>
            <button onClick={syncNow} disabled={busy} className={`${btn} border border-ink hover:bg-ink hover:text-bone`} data-testid="gcal-sync-now"><RefreshCw size={14} /> Sincronizza ora</button>
            <button onClick={disconnect} disabled={busy} className={`${btn} border border-red-700 text-red-700 hover:bg-red-700 hover:text-bone`} data-testid="gcal-disconnect-btn"><Unlink size={14} /> Disconnetti</button>
          </div>
        </div>
      )}
    </section>
  );
}
